<script setup>
import {ref} from "vue";
import {useI18n} from "vue-i18n";
import {useDataTable} from "@/utils/datatableHelper";
import {formatDate, formatNumber} from "@/helper";
import BondService from "@/services/BondService";

/**
 * IMA-517 fáze 5: karta Dluhopisy na detailu strategie typu BOND v administraci.
 * Dluhopisy nejsou na strategii navázané cizím klíčem – pod strategii Dluhopisy patří všechny dluhopisy,
 * proto se používá stejný seznam jako v menu Dluhopisy (api/bonds/list).
 * Karta je přes celou šířku pod horními kartami (Základní informace + Souhrn – viz StrategyBondsSummaryCard).
 */
const {t} = useI18n();

// výchozí řazení podle splatnosti, 10 řádků jako v seznamu Dluhopisů (BondsIndex.vue)
const datatable = useDataTable(fetchBonds, 'maturity_date', 10, true);

/** Barva štítku stavu prodeje – stejná jako v seznamu Dluhopisů v administraci (BondsIndex.vue). */
const saleStateBadge = {
  ON_SALE: 'bg-success',
  PREPARING: 'bg-primary',
  CLOSED: 'bg-secondary',
  UNKNOWN: 'bg-warning text-dark',
};

const columns = ref([
  {label: t('bonds.name'), field: 'name'},
  // ISIN jako samostatný sloupec jako ve vzoru (_Docs/IMA-517-dluhopisy-prototyp.html, detail strategie)
  {label: t('bonds.isin'), field: 'isin'},
  {label: t('bonds.sale_state'), field: 'sale_state', sortable: false},
  {label: t('bonds.coupon_rate'), field: 'coupon_rate', type: 'number'},
  {label: t('bonds.maturity_date'), field: 'maturity_date'},
  {label: t('bonds.clients_count'), field: 'clients_count', type: 'number', sortable: false},
  // detail přes oko jako v seznamu Dluhopisů (BondsIndex.vue) – kvůli konzistenci bez prokliku celým řádkem
  {label: '', field: 'action', sortable: false},
]);

function fetchBonds(body) {
  datatable.isLoading.value = true;
  BondService.fetchRecords(body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    datatable.rows.value = response.data;
    datatable.isLoading.value = false;
  });
}
</script>

<template>
  <div class="card">
    <div class="card-header">
      <div class="row">
        <div class="col">
          <div class="card-header__title">{{ t('strategiesDetail.bonds.title') }}</div>
        </div>
        <div class="col-auto text-end">
          <router-link :to="{name: 'Securities'}" class="btn btn-link p-0">{{ t('strategiesDetail.bonds.manage') }}</router-link>
        </div>
      </div>
    </div>
    <div class="card-body">
      <vue-good-table
          :columns="columns"
          :rows="datatable.rows.value"
          mode="remote"
          @page-change="datatable.onPageChange"
          @sort-change="datatable.onSortChange"
          @per-page-change="datatable.onPerPageChange"
          :totalRows="datatable.totalRecords.value"
          :pagination-options="{
                  enabled: true,
                  perPage: 10,
                  perPageDropdownEnabled: false,
                  nextLabel: t('strategies.next'),
                  prevLabel: t('strategies.previous'),
                  ofLabel: t('strategies.of'),
                }">
        <template #table-row="props">
          <span v-if="props.column.field === 'isin'">{{ props.row.isin ?? '---' }}</span>
          <span v-else-if="props.column.field === 'sale_state'" class="badge" :class="saleStateBadge[props.row.sale_state]">{{ t('bonds.sale_states.' + props.row.sale_state) }}</span>
          <span v-else-if="props.column.field === 'coupon_rate'">{{ props.row.coupon_rate === null ? '---' : formatNumber(props.row.coupon_rate) + ' %' }}</span>
          <span v-else-if="props.column.field === 'maturity_date'">{{ props.row.maturity_date ? formatDate(props.row.maturity_date, 'd.m.Y') : '---' }}</span>
          <span v-else-if="props.column.field === 'action'">
            <span style="font-weight: bold; float: right">
              <router-link :to="{ name: 'SecurityDetail', params: { id: props.row.id} }"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
            </span>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] ?? '---' }}</span>
        </template>
        <template v-slot:emptystate v-if="!datatable.isLoading.value">
          <div class="text-center">{{ t('bonds.no_records_found') }}</div>
        </template>
        <template v-slot:emptystate v-if="datatable.isLoading.value">
          <div class="text-center">{{ t('strategies.data_loading') }}</div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>
